import client from '@/services/';

const state = {
    soldePrecedent: null,
    listeSoldes: [],
    statistiques: {},
    loading: false,
    error: null
};

const getters = {
    getSoldePrecedent: state => state.soldePrecedent,
    getListeSoldes: state => state.listeSoldes,
    getStatistiques: state => state.statistiques,
    isLoading: state => state.loading,
    getError: state => state.error,

    // Indique si l'expert a une dette
    hasDette: state => state.soldePrecedent && state.soldePrecedent.type === 'DETTE',

    // Indique si l'expert a une créance
    hasCreance: state => state.soldePrecedent && state.soldePrecedent.type === 'CREANCE',

    // Retourne le montant restant du solde
    montantRestant: state => state.soldePrecedent ? state.soldePrecedent.montantRestant : 0
};

const mutations = {
    SET_SOLDE_PRECEDENT(state, solde) {
        state.soldePrecedent = solde;
    },
    SET_LISTE_SOLDES(state, liste) {
        state.listeSoldes = liste;
    },
    SET_STATISTIQUES(state, stats) {
        state.statistiques = stats;
    },
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    CLEAR_SOLDE(state) {
        state.soldePrecedent = null;
    }
};

const actions = {
    /**
     * Récupère le solde actif de l'expert connecté
     */
    async getMonSolde({ commit }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
            const response = await client.get(`soldes-precedents/mon-solde`);
            commit('SET_SOLDE_PRECEDENT', response.data);
            return response.data;
        } catch (error) {
            // Si pas de solde trouvé, ce n'est pas une erreur
            if (error.response && error.response.status === 404) {
                commit('SET_SOLDE_PRECEDENT', null);
                return null;
            }
            commit('SET_ERROR', error.message);
            commit('SET_SOLDE_PRECEDENT', null);
            console.log('Aucun solde précédent trouvé');
            return null;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    /**
     * Récupère le solde d'un expert par son ID
     */
    async getSoldeExpert({ commit }, expertId) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
            const response = await client.get(`soldes-precedents/expert/${expertId}`);
            commit('SET_SOLDE_PRECEDENT', response.data);
            return response.data;
        } catch (error) {
            // Si pas de solde trouvé, ce n'est pas une erreur
            if (error.response && error.response.status === 404) {
                commit('SET_SOLDE_PRECEDENT', null);
                return null;
            }
            commit('SET_ERROR', error.message);
            commit('SET_SOLDE_PRECEDENT', null);
            console.log('Aucun solde précédent trouvé pour cet expert');
            return null;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    /**
     * Récupère le solde d'une société
     */
    async getSoldeSociete({ commit }, societeId) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
            const response = await client.get(`soldes-precedents/societe/${societeId}`);
            commit('SET_SOLDE_PRECEDENT', response.data);
            return response.data;
        } catch (error) {
            if (error.response && error.response.status === 404) {
                commit('SET_SOLDE_PRECEDENT', null);
                return null;
            }
            commit('SET_ERROR', error.message);
            commit('SET_SOLDE_PRECEDENT', null);
            console.log('Aucun solde précédent trouvé pour cette société');
            return null;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    /**
     * Récupère tous les soldes (admin)
     */
    async getAllSoldes({ commit }, params = {}) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
            const response = await client.get(`soldes-precedents/all`, { params });
            commit('SET_LISTE_SOLDES', response.data.content || response.data);
            return response.data;
        } catch (error) {
            commit('SET_ERROR', error.message);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    /**
     * Crée un nouveau solde (admin)
     */
    async creerSolde({ dispatch }, soldeData) {
        try {
            const response = await client.post(`soldes-precedents`, soldeData);
            // Recharger la liste après création
            await dispatch('getAllSoldes', { page: 0, size: 20 });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Régularise un solde (lors du paiement)
     * @param {Object} payload - { soldeId, montant, typeMaintien }
     * @param {String} typeMaintien - "PHYSIQUE" ou "SOCIETE" (optionnel, par défaut "PHYSIQUE")
     */
    async regulariser({ commit }, { soldeId, montant, typeMaintien }) {
        try {
            const params = { montant };

            // Ajouter typeMaintien si fourni
            if (typeMaintien) {
                params.typeMaintien = typeMaintien;
            }

            const response = await client.post(
                `soldes-precedents/${soldeId}/regulariser`,
                null,
                { params }
            );
            commit('SET_SOLDE_PRECEDENT', response.data);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Importe les soldes depuis Excel (admin)
     */
    async importerSoldes({ dispatch }, { file, type }) {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', type); // DETTE ou CREANCE

            const response = await client.post(
                `soldes-precedents/import`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            // Recharger la liste après import
            await dispatch('getAllSoldes', { page: 0, size: 20, actifsOnly: true });

            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Récupère les statistiques (admin)
     */
    async getStatistiques({ commit }) {
        try {
            const response = await client.get(`soldes-precedents/statistiques`);
            commit('SET_STATISTIQUES', response.data);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Efface le solde du store
     */
    clearSolde({ commit }) {
        commit('CLEAR_SOLDE');
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};

